import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen, Mountain, Music2, Sparkles, Mail, FileText } from "lucide-react";
import { owner, journey } from "../data/content";
import LatestBlogCard from "../components/LatestBlogCard";

const tiles = [
  { to: "/books", label: "Books", desc: "What I'm reading, and what I keep coming back to.", icon: BookOpen, testid: "tile-books" },
  { to: "/trek-and-travel", label: "Trek & Travel", desc: "The trails of the last twelve months.", icon: Mountain, testid: "tile-trek" },
  { to: "/music", label: "Music Corner", desc: "Songs recorded at home, in the slow hours.", icon: Music2, testid: "tile-music" },
  { to: "/ai-media", label: "AI Media", desc: "Old songs, new tools, careful credits.", icon: Sparkles, testid: "tile-ai" },
  { to: "/blogs", label: "Blogs", desc: "Longer notes — in English and Bengali — from the road and the page.", icon: FileText, testid: "tile-blogs" },
  { to: "/contact", label: "Write to me", desc: "A short note is always welcome.", icon: Mail, testid: "tile-contact" },
];

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative pt-16 sm:pt-24 pb-10 grain">
        <div className="container-narrow grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <p className="eyebrow" data-testid="hero-eyebrow">
              Hello — and welcome
            </p>
            <h1 className="font-serif mt-5 text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#2C2A28]">
              I am <span className="italic text-[#4A6B53]">{owner.name.split(" ")[0]}</span>.
              <br />
              These are my <span className="italic">slow, sunlit</span> days.
            </h1>
            <p className="mt-7 max-w-xl text-lg text-[#5C5852] leading-relaxed">
              {owner.shortBio}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link
                to="/books"
                data-testid="hero-cta-books"
                className="inline-flex items-center gap-2 px-7 py-3 rounded-sm bg-[#4A6B53] text-[#FAF9F6] hover:bg-[#3A5441] transition-colors duration-300 text-sm tracking-wide"
              >
                Begin with the bookshelf
                <ArrowUpRight size={16} />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-contact"
                className="inline-flex items-center gap-2 px-7 py-3 rounded-sm border-2 border-[#4A6B53] text-[#4A6B53] hover:bg-[#4A6B53]/10 transition-colors duration-300 text-sm tracking-wide"
              >
                Write to me
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.85, delay: 0.1 }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div className="absolute -inset-3 sm:-inset-5 border border-[#CC7A52] rounded-sm translate-x-3 translate-y-3 sm:translate-x-5 sm:translate-y-5" />
              <div className="relative overflow-hidden rounded-sm border border-[#D8D3CC] bg-[#F2EFEB]">
                <img
                  src={owner.profilePhoto}
                  alt={`Portrait of ${owner.name}`}
                  className="w-full h-auto object-contain"
                  data-testid="hero-profile-photo"
                />
              </div>
              <div className="mt-4 text-xs text-[#5C5852] tracking-wide italic">
                "{owner.tagline}"
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* JOURNEY — Two narrative sections, alternating */}
      <section className="py-20 sm:py-28">
        <div className="container-narrow grid lg:grid-cols-2 gap-12 lg:gap-20">
          {[journey.earlyYears, journey.career].map((entry, i) => (
            <motion.article
              key={entry.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className={`relative ${i === 1 ? "lg:mt-16" : ""}`}
              data-testid={`journey-card-${i}`}
            >
              <p className="eyebrow">{entry.eyebrow}</p>
              <h2 className="font-serif text-4xl sm:text-5xl mt-3 text-[#2C2A28] leading-tight">
                {entry.title}
              </h2>
              <div className="mt-6 space-y-5 text-[#5C5852] leading-relaxed text-lg">
                {entry.paragraphs.map((p, idx) => (
                  <p key={idx}>{p}</p>
                ))}
              </div>
              <div className="mt-8 h-[1px] w-16 bg-[#CC7A52]" />
            </motion.article>
          ))}
        </div>
      </section>

      {/* LATEST BLOG */}
      <LatestBlogCard />

      {/* EXPLORE TILES */}
      <section className="pb-24">
        <div className="container-narrow">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <p className="eyebrow">Wander further</p>
              <h2 className="font-serif text-4xl sm:text-5xl mt-3 text-[#2C2A28]">
                Six quiet rooms.
              </h2>
            </div>
            <p className="text-[#5C5852] text-sm max-w-sm">
              Take any door — books, mountains, music, AI experiments, blog
              entries, or simply say hello.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {tiles.map((t, i) => {
              const Icon = t.icon;
              return (
                <motion.div
                  key={t.to}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                >
                  <Link
                    to={t.to}
                    data-testid={t.testid}
                    className="card-lift block h-full p-7 sm:p-8 rounded-sm bg-[#F2EFEB] border border-[#D8D3CC] group"
                  >
                    <div className="flex items-start justify-between">
                      <Icon size={26} className="text-[#4A6B53]" strokeWidth={1.4} />
                      <ArrowUpRight
                        size={18}
                        className="text-[#5C5852] group-hover:text-[#CC7A52] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-300"
                      />
                    </div>
                    <h3 className="font-serif text-2xl mt-7 text-[#2C2A28]">
                      {t.label}
                    </h3>
                    <p className="mt-3 text-sm text-[#5C5852] leading-relaxed">
                      {t.desc}
                    </p>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
